import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Phone, Mail, MapPin, Send, Calendar, CheckCircle } from 'lucide-react';
import SuccessModal from '../components/SuccessModal';
import InlineAuth from '../components/InlineAuth';
import { useCustomerAuth, getCustomerToken } from '../context/CustomerAuthContext';

const ContactPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { customer } = useCustomerAuth();
  const selectedDestination = location.state?.destination || '';
  const selectedActivities = location.state?.activities || [];
  const inquiryType = location.state?.inquiryType || 'general';
  const itinerary = location.state?.itinerary || [];
  const basedOn = location.state?.basedOn || '';
  
  const [formData, setFormData] = useState(() => {
    // Generate initial message based on inquiry type
    let initialMessage = '';
    if (inquiryType === 'activities' && selectedActivities.length > 0) {
      initialMessage = `Selected Activities for ${selectedDestination}:\n${selectedActivities.map(a => `• ${a.name}`).join('\n')}\n\nAdditional Requirements:\n`;
    } else if (inquiryType === 'custom-itinerary' && itinerary.length > 0) {
      let msg = `Custom Itinerary Request for ${selectedDestination}\nBased on: ${basedOn}\n\n`;
      itinerary.forEach(day => {
        msg += `Day ${day.day}: ${day.title}\n`;
        day.activities.forEach(act => {
          msg += `  • ${act}\n`;
        });
        msg += '\n';
      });
      msg += 'Additional Requirements:\n';
      initialMessage = msg;
    } else if (inquiryType === 'direct') {
      initialMessage = `I am interested in traveling to ${selectedDestination}.\n\nMy requirements:\n`;
    } else if (inquiryType === 'visa') {
      initialMessage = `I would like to apply for a visa (${selectedDestination}).\n\nPlease guide me with the process. I can upload my documents in my account chat.\n`;
    }
    
    return {
      name: '',
      email: '',
      phone: '',
      destination: selectedDestination,
      travelDates: '',
      travelers: '',
      message: initialMessage
    };
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAuthGate, setShowAuthGate] = useState(false);

  // Prefill contact details for logged-in customers
  useEffect(() => {
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || customer.name || '',
        email: prev.email || customer.email || '',
        phone: prev.phone || customer.phone || '',
      }));
      setShowAuthGate(false);
    }
  }, [customer]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const submitInquiry = async (override = {}) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = getCustomerToken();
      const payload = {
        name: override.name || formData.name,
        email: override.email || formData.email,
        phone: override.phone || formData.phone,
        destination: formData.destination || null,
        travel_dates: formData.travelDates || null,
        travelers: formData.travelers ? parseInt(formData.travelers) : null,
        message: formData.message || null,
        inquiry_type: inquiryType,
      };
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowSuccess(true);
        setShowAuthGate(false);
        setFormData({
          name: customer?.name || override.name || '',
          email: customer?.email || override.email || '',
          phone: customer?.phone || override.phone || '',
          destination: '',
          travelDates: '',
          travelers: '',
          message: '',
        });
      } else {
        console.error('Failed to submit inquiry');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Account is required to send an inquiry — reveal the inline auth gate first.
    if (!customer) {
      setShowAuthGate(true);
      return;
    }
    submitInquiry();
  };

  // After the visitor logs in / creates an account, send the inquiry immediately.
  const handleAuthed = (authedCustomer) => {
    submitInquiry({
      name: formData.name || authedCustomer?.name,
      email: formData.email || authedCustomer?.email,
      phone: formData.phone || authedCustomer?.phone,
    });
  };

  // Render inquiry summary card
  const renderInquirySummary = () => {
    if (inquiryType === 'activities' && selectedActivities.length > 0) {
      return (
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center text-amber-800">
              <CheckCircle className="w-5 h-5 mr-2" />
              Selected Activities for {selectedDestination}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {selectedActivities.map((activity, idx) => (
                <span key={idx} className="bg-white px-3 py-1 rounded-full text-sm border border-amber-200">
                  {activity.name}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (inquiryType === 'custom-itinerary' && itinerary.length > 0) {
      return (
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center text-amber-800">
              <Calendar className="w-5 h-5 mr-2" />
              Custom Itinerary for {selectedDestination}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-700 mb-3">Based on: {basedOn}</p>
            <div className="space-y-2">
              {itinerary.map((day, idx) => (
                <div key={idx} className="bg-white p-3 rounded-lg border border-amber-200">
                  <p className="font-semibold text-gray-800">Day {day.day}: {day.title}</p>
                  <p className="text-sm text-gray-600">{day.activities.join(' → ')}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <>
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Plan Your Dream Trip
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get in touch with us to start planning your perfect vacation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-l-4 border-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Phone className="w-5 h-5 text-amber-600 mr-2" />
                  Phone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  <a href="tel:+916358846179" className="block text-gray-700 hover:text-amber-600 transition-colors">+91 63588 46179</a>
                  <a href="tel:+919924546179" className="block text-gray-700 hover:text-amber-600 transition-colors">+91 99245 46179</a>
                  <a href="tel:+919924646179" className="block text-gray-700 hover:text-amber-600 transition-colors">+91 99246 46179</a>
                  <a href="tel:+919081846179" className="block text-gray-700 hover:text-amber-600 transition-colors">+91 90818 46179</a>
                </div>
                <p className="text-sm text-gray-500 mt-3">Open daily: 11:00 AM - 11:00 PM</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Mail className="w-5 h-5 text-amber-600 mr-2" />
                  Email
                </CardTitle>
              </CardHeader>
              <CardContent>
                <a href="mailto:Info@Thenavkartravels.com" className="text-gray-700 hover:text-amber-600 transition-colors">
                  Info@Thenavkartravels.com
                </a>
                <p className="text-sm text-gray-500 mt-1">24/7 Support</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <MapPin className="w-5 h-5 text-amber-600 mr-2" />
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <a href="https://share.google/wnPvv83B3tBR31ZJ3" target="_blank" rel="noopener noreferrer" data-testid="contact-address" className="block not-italic text-gray-700 leading-relaxed hover:text-amber-600 transition-colors">
                  The Navkar Travels,<br />
                  Opposite Shreeji Arcade, Adajan,<br />
                  Surat - 395005, Gujarat, India
                </a>
                <p className="text-sm text-amber-600 mt-2 font-medium">Tap to open in Google Maps →</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {/* Inquiry Summary */}
            {renderInquirySummary()}

            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">Send Us Your Travel Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Destination
                      </label>
                      <Input
                        type="text"
                        name="destination"
                        value={formData.destination}
                        onChange={handleChange}
                        placeholder="e.g., Bali, Dubai, Europe"
                        data-testid="input-destination"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Travel Dates
                      </label>
                      <Input
                        type="text"
                        name="travelDates"
                        value={formData.travelDates}
                        onChange={handleChange}
                        placeholder="e.g., Jan 2026"
                        data-testid="input-travel-dates"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Travelers
                      </label>
                      <Input
                        type="number"
                        name="travelers"
                        value={formData.travelers}
                        onChange={handleChange}
                        placeholder="2"
                        min="1"
                        data-testid="input-travelers"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Requirements & Budget
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      placeholder="Tell us about your travel preferences, budget, and any special requirements..."
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6"
                    data-testid="submit-inquiry"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Inquiry
                  </Button>
                </form>

                {/* Account required to send — inline login / create account gate */}
                {!customer && showAuthGate && (
                  <div className="mt-6 border-t pt-6" data-testid="contact-auth-gate">
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">One last step</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Please log in or create a free account to send your inquiry. This lets you track
                        your request and receive the exact itinerary from our team.
                      </p>
                      <InlineAuth onSuccess={handleAuthed} />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>

    <SuccessModal open={showSuccess} onClose={() => setShowSuccess(false)} />
    </>
  );
};

export default ContactPage;
